import MainButton from '@/components/ui/custom/buttons/MainButton';
import InfinityList from '@/components/ui/custom/InfinityList';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useManualQuery from '@/data/hook/query/v1/useManualQuery';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { createHackathonStage } from '@/data/services/hackathonStagesServices';
import { hackathonsStagesQueryKeys } from '@/data/services/querykeys';
import Hackathon, { HackathonStage, StageTypeEnum } from '@/models/Hackathon';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  createNextStage,
  createDefaultStage,
  getLastStage,
} from '@/utils/hackathon';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import alert from '@/utils/shared/alert';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { twMerge } from 'tailwind-merge';
import HackathonStageService from './HackathonStageService';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';

const HackathonStagesService = ({ hackathon }: { hackathon: Hackathon }) => {
  const { queryFn, queryKey } = hackathonsStagesQueryKeys.list({
    hackathon: hackathon.id,
  });
  const {
    results: hackathonStages,
    fetchNextPage,
    hasNextPage,
    isInitialLoading,
    isFetchingNextPage,
  } = useInfiniteService({
    queryFn,
    queryKey,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { add, remove, update } = useManualQuery({ queryKey });
  const { mutate: onAdd, isLoading: isAdding } = useMutation(
    createHackathonStage,
    {
      onSuccess: data => {
        add(data, { position: 'end' });
      },
      onError: e => alert.error(getErrorMessage(e)),
    },
  );

  const sortedHackathonStages = [...hackathonStages].sort((a, b) => {
    const stageOrder = {
      [StageTypeEnum.SUBMISSION]: 0,
      [StageTypeEnum.INITIAL_VOTING]: 1,
      [StageTypeEnum.FINAL_VOTING]: 2,
    };
    return stageOrder[a.type] - stageOrder[b.type];
  });

  const getNextStage = (): Omit<HackathonStage, 'id'> => {
    const lastStage = getLastStage(hackathonStages);
    if (!lastStage) return createDefaultStage(hackathon);
    const nextStage = createNextStage(lastStage);
    return nextStage;
  };

  return (
    <InfinityList
      className={twMerge('h-96 w-full pr-2 gap-4', scrollBarYClassName)}
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      <ConditionalRenderer condition={isInitialLoading}>
        <div className="w-full h-6 flex items-center justify-center">
          <LoadingIcon className="text-primary w-6 h-6" />
        </div>
      </ConditionalRenderer>
      {sortedHackathonStages.map(stage => (
        <HackathonStageService
          key={stage.id}
          hackathonStage={stage}
          hackathon={hackathon}
          onDelete={remove}
          onSuccess={update}
        />
      ))}
      <MainButton
        text="Adicionar"
        icon={<PlusIcon />}
        onClick={() => onAdd(getNextStage())}
        loading={isAdding}
      />
    </InfinityList>
  );
};

export default HackathonStagesService;
